/**
 * Quoted-reply boundary detection — pure, DOM-free, dependency-free.
 *
 * When a thread is rendered as a stack of per-message cards (Gmail/Outlook
 * style), the quoted history embedded in each message body is redundant with
 * the cards above it — and nested reply quotes stack one `<blockquote>` deeper
 * per round, producing the vertical-line "nesting" that reads as noise. This
 * module classifies whether a given top-level element in an email body begins
 * the quoted trailer, so the renderer can collapse everything from that element
 * down behind a "show quoted text" toggle (the quote still ships on the wire for
 * external recipients — only the DISPLAY collapses).
 *
 * The logic is intentionally pure: the caller extracts a {@link QuoteBoundaryDescriptor}
 * from each top-level DOM node and asks {@link isQuotedReplyBoundary}. That keeps
 * the decision unit-testable without a DOM while the DOM walk stays trivial.
 */

/** A flattened view of a top-level email body element — the only inputs the boundary decision needs. */
export interface QuoteBoundaryDescriptor {
  /** Lowercased tag name, e.g. `"blockquote"`, `"div"`, `"hr"`. */
  tagName: string;
  /** The element's `id` attribute (empty string when absent). */
  id: string;
  /** The element's `class` attribute (empty string when absent). */
  className: string;
  /** The element's trimmed, whitespace-collapsed text content. */
  text: string;
}

/**
 * Container ids used by common clients to wrap the quoted / forwarded trailer.
 * Matched case-insensitively against the element `id`.
 */
const QUOTE_CONTAINER_IDS: readonly string[] = [
  "divrplyfwdmsg", // Outlook desktop reply/forward divider container
  "appendonsend", // Outlook (new) — marker div preceding the quoted history
  "mail-editor-reference-message-container", // Outlook web
];

/**
 * `class` fragments used by common clients to wrap the quoted trailer.
 * Matched case-insensitively as substrings of the element `class`.
 */
const QUOTE_CONTAINER_CLASSES: readonly string[] = [
  "gmail_quote", // Gmail (also on the wrapping <div> and the inner <blockquote>)
  "gmail_attr", // Gmail "On … wrote:" attribution line
  "moz-cite-prefix", // Thunderbird attribution
];

/**
 * Attribution line that introduces a quote when the quote is NOT wrapped in a
 * recognizable container, e.g. a bare `On Mon, Jan 1, 2026 at 9:00 AM, A B
 * <a@x.com> wrote:`. Bounded length keeps a stray "…wrote:" deep in prose from
 * matching. Case-insensitive; whitespace is collapsed by the caller.
 */
const ATTRIBUTION_RE = /^on\b[\s\S]{0,400}\bwrote:\s*$/i;

/** Forwarded-message header line, e.g. `---------- Forwarded message ----------`. */
const FORWARDED_HEADER_RE = /^-{2,}\s*forwarded message\s*-{2,}/i;

function hasAny(haystack: string, needles: readonly string[]): boolean {
  const lower = haystack.toLowerCase();
  return needles.some((n) => lower.includes(n));
}

/**
 * True when `d` is the first element of the quoted / forwarded trailer — i.e.
 * everything from this element to the end of the body is quoted history that
 * should collapse behind a toggle.
 *
 * Structural markers (tag / id / class) are preferred because they are
 * language-independent and unambiguous; the textual attribution / forwarded
 * heuristics are the fallback for bare quotes with no wrapper.
 */
export function isQuotedReplyBoundary(d: QuoteBoundaryDescriptor): boolean {
  const tag = d.tagName.toLowerCase();

  // A top-level <blockquote> is the canonical quoted trailer (our own reply
  // format, Apple Mail `type="cite"`, Gmail's inner quote, etc.).
  if (tag === "blockquote") return true;

  if (d.id && hasAny(d.id, QUOTE_CONTAINER_IDS)) return true;
  if (d.className && hasAny(d.className, QUOTE_CONTAINER_CLASSES)) return true;

  const text = d.text.trim();
  if (!text) return false;
  if (ATTRIBUTION_RE.test(text)) return true;
  if (FORWARDED_HEADER_RE.test(text)) return true;

  return false;
}
